import ComponentGuard from '@/components/ui/custom/ComponentGuard';
import Text from '@/components/ui/custom/dataDisplay/Text';
import ChoiceSelector from '@/components/ui/custom/dataInput/ChoiceSelector';
import Skeleton from '@/components/ui/custom/Skeleton';
import { REQUEST_STALE_TIME_IN_MS } from '@/constants';
import { courseBasesQueryKeys } from '@/data/services/querykeys';
import { ContentCreatorCourse, Course } from '@/models/Course';
import { UserTypeEnum } from '@/models/User';
import {
  nameAndFormats,
  segregateToCreateAndDelete,
} from '@/utils/courseBase';
import { Control, useController } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { TeamFormFields } from './TeamForm';

type ManageContentCreatorCoursesProps = {
  control: Control<TeamFormFields>;
  contentCreatorCourses: ContentCreatorCourse[];
  isLoading?: boolean;
  disabled?: boolean;
};

export default function ManageContentCreatorCourses({
  contentCreatorCourses,
  isLoading,
  control,
  disabled,
}: ManageContentCreatorCoursesProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'manageTeam.course',
  });
  const contentCreateCoursesField = useController({
    control,
    name: 'contentCreatorCourses',
  });

  const onChange = (courses: Course[]) => {
    const coursesIdsChange = courses.map(course => course.id);
    const { toDelete, toCreate } = segregateToCreateAndDelete(
      contentCreatorCourses,
      coursesIdsChange,
    );

    contentCreateCoursesField.field.onChange({ toDelete, toCreate });
  };

  if (isLoading) {
    return <Skeleton className="h-8 w-full rounded-md bg-primary-content" />;
  }

  if (disabled) {
    return (
      <div className="flex">
        <Text text={t('label')} className="font-500 text-14 w-32" />
        <ul className="flex flex-col gap-0.5 flex-wrap justify-end ml-1.5">
          {contentCreatorCourses.map(contentCreatorCourse => (
            <ChoiceSelectRender
              key={contentCreatorCourse.course.id}
              model={contentCreatorCourse.course}
            />
          ))}
        </ul>
      </div>
    );
  }

  return (
    <ComponentGuard roles={[UserTypeEnum.SUPER_ADMIN]}>
      <ChoiceSelector
        service={courseBasesQueryKeys.list}
        getKey={courseBase => courseBase.id}
        displayName={courseBase => courseBase.name}
        render={ChoiceSelectRender}
        options={{ staleTime: REQUEST_STALE_TIME_IN_MS }}
        defaultValue={contentCreatorCourses.map(
          contentCreatorCourse => contentCreatorCourse.course,
        )}
        className={{
          ul: 'ml-32 w-[calc(100%-8rem)]',
        }}
        input={{
          label: t('label'),
          placeholder: t('placeholder'),
          labelPosition: 'left',
          className: {
            label: 'font-500 text-14',
            input: '',
          },
        }}
        onChange={onChange}
      />
    </ComponentGuard>
  );
}

type ChoiceSelectRenderProps = {
  model: Course;
};

const ChoiceSelectRender = ({ model }: ChoiceSelectRenderProps) => {
  return (
    <Text
      text={nameAndFormats(model.name, model.formats)}
      className="cursor-pointer pl-5 font-500 text-14"
      format="rubik-400"
    />
  );
};
